<script setup lang="ts">
import { provide, type Ref, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { DeleteDialog, CreateOrUpdateDialog } from '@haskoning/vue-component-library'
import CalculationIndex from '@/components/calculation-drawer/CalculationIndex.vue'
import CalculationViewDialog from '@/components/calculation/CalculationViewDialog.vue'
import OptionsDrawer from '@/components/drawer/OptionsDrawer.vue'
import CalculationCreateDialog from '@/components/calculation/CalculationCreateDialog.vue'
import ReportDialog from '@/components/dialog/ReportDialog.vue'
import ExportExcelDialog from '@/components/dialog/ExportExcelDialog.vue'

import { useGiPointStore } from '@/stores/giPoints'
import { useProjectStore } from '@/stores/project'
import { useCalculationStore } from '@/stores/calculation'
import { useCalculationCPTStore } from '@/stores/calculationCPT'
import { useGiDataUploadCollectionStore } from '@/stores/giDataUploadCollection'

import type { CalculationType } from '@/stores/type/calculation.type'

const giPointStore = useGiPointStore()
const projectStore = useProjectStore()
const calculationStore = useCalculationStore()
const calculationCPTStore = useCalculationCPTStore()
const giDataUploadCollectionStore = useGiDataUploadCollectionStore()

const {
	currentItem: currentCalculation,
	writeLoading: loading,
	items: calculationItems,
	selectedItems: selectedCalculations,
	deleteDialog: calculationDeleteDialog,
	deleteLoading: calculationDeleteLoading,
	createUpdateDialog,
	fieldsFiltered
} = storeToRefs(calculationStore)
const {
	currentItem: currentCalculationCPT,
	selectedItems: selectedCalculationCPTs,
	deleteDialog: calculationCPTDeleteDialog,
	deleteLoading: calculationCPTDeleteLoading
} = storeToRefs(calculationCPTStore)

const updateCalculation = async (item: CalculationType) => {
	await calculationStore.update(item)
	createUpdateDialog.value = false
}

const onDeleteCurrentCalculationCPT = () => {
	projectStore.toggleResultDrawer(false)
	calculationCPTStore.setCurrentItem({})
}

const onCalculationDelete = (deletedItems: CalculationType[]) => {
	deletedItems.forEach((item) => {
		if (currentCalculationCPT.value?.calculation === item.id) {
			onDeleteCurrentCalculationCPT()
		}
		calculationCPTStore.removeItemsByCalculationId(item.id)
	})
	calculationStore.clearSelectedItems()
	calculationCPTStore.clearSelectedItems()
}

const onCalculationCPTDelete = () => {
	if (currentCalculationCPT.value?.id) {
		onDeleteCurrentCalculationCPT()
	}
	selectedCalculationCPTs.value.forEach((item) => {
		calculationItems.value.forEach((calculation) => {
			calculation.calculationcpt_set = calculation.calculationcpt_set.filter(
				(cpt) => cpt.id !== item.id
			)
		})
	})
	calculationStore.clearSelectedItems()
	calculationCPTStore.clearSelectedItems()
}

const calculationsViewShowDialog = ref<boolean>(false)
provide<Ref<boolean>>('calculationsViewShowDialog', calculationsViewShowDialog)

watch(calculationsViewShowDialog, (value) => {
	if (!value) {
		giDataUploadCollectionStore.setCurrentItem({})
	}
})

watch(createUpdateDialog, () => {
	giPointStore.clearSelectedItems()
	giDataUploadCollectionStore.clearSelectedItems()
})
</script>

<template>
	<OptionsDrawer state-name="calculationDrawer" :temporary="true">
		<v-card elevation="0" class="pb-3 px-3">
			<v-card-title class="component-title px-0"> Calculation management</v-card-title>
			<v-card-subtitle class="component-subtitle px-0"
				>Manage calculations and visualize the liquefaction results
			</v-card-subtitle>
		</v-card>
		<v-divider />
		<v-lazy>
			<CalculationIndex></CalculationIndex>
		</v-lazy>
		<CalculationViewDialog></CalculationViewDialog>

		<DeleteDialog
			bulk-delete
			v-model="calculationDeleteDialog"
			:loading="calculationDeleteLoading"
			:items="selectedCalculations"
			@bulk-delete="calculationStore.bulkDelete(selectedCalculations)"
			@submitted-and-closed="onCalculationDelete"
		/>
		<DeleteDialog
			bulk-delete
			v-model="calculationCPTDeleteDialog"
			:loading="calculationCPTDeleteLoading"
			:items="selectedCalculationCPTs"
			@bulk-delete="calculationCPTStore.bulkDelete(selectedCalculationCPTs)"
			@submitted-and-closed="onCalculationCPTDelete"
		/>
		<CalculationCreateDialog />
		<CreateOrUpdateDialog
			v-if="currentCalculation"
			v-model="createUpdateDialog"
			:loading="loading"
			v-model:item="currentCalculation"
			name="calculation"
			:fields="fieldsFiltered"
			@submit="updateCalculation"
		/>
		<ExportExcelDialog />
		<ReportDialog />
	</OptionsDrawer>
</template>
